import React from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiCalendar, FiCheck } from 'react-icons/fi';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import { cn } from '@/lib/utils';
import { statusBadgeText } from '../utils/BadgeEssay';
import essayIcon from '@/assets/images/icons/essay.png';

const STATUS_BADGE_VARIANT = {
  accepting_proposal: 'info-soft',
  processing: 'amber-soft',
  reviewing: 'amber-soft',
  ocr_review: 'amber-soft',
  sending_by_teacher: 'amber-soft',
  unreviewed: 'success-soft',
  reviewed: 'success-soft',
  annulled: 'danger-soft',
  annulled_by_teacher: 'danger-soft',
  minimum_length_not_satisfied: 'danger-soft',
  failed_to_process: 'danger-soft'
};

const STATUS_BADGE_FLAT_CLASSNAME = {
  close_for_submissions: 'bg-transparent text-neutral-500 shadow-none',
  not_sent: 'bg-transparent text-neutral-500 shadow-none'
};

export function EssayPlayfulCard({ essay }) {
  const navigate = useNavigate();

  const accepting_proposal = essay?.status === 'accepting_proposal';
  const grade = essay?.['essay-student']?.grade ?? 0;

  const meta = [];
  if (accepting_proposal || essay?.status === 'close_for_submissions' || essay?.status === 'ocr_review') {
    meta.push({ icon: FiCalendar, text: `Até ${moment(essay?.['delivery-date']).format('DD/MM/YYYY')}` });
  } else if (['processing', 'annulled', 'annulled_by_teacher'].includes(essay?.status)) {
    meta.push({ icon: FiCalendar, text: `Em ${moment(essay?.['essay-student']?.['delivery-date']).format('DD/MM/YYYY')}` });
    meta.push({ text: `${essay?.['essay-student']?.['submission_order']}° envio` });
  } else if (['reviewed', 'unreviewed', 'reviewing'].includes(essay?.status)) {
    meta.push({ icon: FiCheck, text: `Pontuação ${grade}` });
    meta.push({ text: `${essay?.['essay-student']?.['submission_order']}° envio` });
  }

  const isDisabled = essay?.status === 'close_for_submissions';

  const handleAccess = () => {
    if (['reviewed', 'unreviewed', 'reviewing'].includes(essay?.status)) {
      navigate(`/redacao/consulta/${essay?.id}/${essay?.['essay-student']?.id}`);
    } else if (essay?.status === 'ocr_review') {
      navigate(`/redacao/enviar/${essay?.id}`);
    } else {
      navigate(`/redacao/sobre/${essay?.id}`);
    }
  };

  return (
    <PlayfulCard
      badge={{ text: statusBadgeText[essay?.status], variant: STATUS_BADGE_VARIANT[essay?.status], className: cn('rounded-full px-2.5 py-1 text-xs font-bold normal-case', STATUS_BADGE_FLAT_CLASSNAME[essay?.status]) }}
      className="p-6"
      onClick={isDisabled ? undefined : handleAccess}
    >
      <img
        src={essayIcon}
        alt=""
        className="h-20 w-20 object-contain"
      />

      <h3 className="mb-0 mt-3 line-clamp-3 text-base font-extrabold text-neutral-900">{essay?.topic}</h3>

      <div className="mt-auto flex flex-col gap-3 pt-2">
        {meta.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs font-semibold text-neutral-dark">
            {meta.map((item, index) => (
              <span
                key={index}
                className="flex items-center gap-1.5"
              >
                {item.icon && <item.icon className={cn('h-3.5 w-3.5', !isDisabled && 'text-mission')} />}
                {item.text}
              </span>
            ))}
          </div>
        )}

        <PlayfulCardButton
          disabled={isDisabled}
          onClick={handleAccess}
          className="py-3"
        >
          Acessar
        </PlayfulCardButton>
      </div>
    </PlayfulCard>
  );
}
